import * as React from "react";
import * as Storage from "aws-amplify/storage";
import styled, { css } from "styled-components";

export interface S3ImageProps {
  className?: string;
  placeholder?: string;
  s3Key: string | undefined;
  level: "public" | "protected" | "private";
  alt: string;
}

function blobToBase64(blob: Blob): Promise<string | null> {
  return new Promise((resolve, _) => {
    const reader = new FileReader();

    reader.onloadend = () => resolve(reader.result?.toString() || null);
    reader.readAsDataURL(blob);
  });
}

const S3Image: React.ReactFCWithChildren<S3ImageProps> = ({
  className, placeholder, s3Key, level, alt
}) => {
  const [ imgData, setImgData ] = React.useState<string | undefined>();
  const [ loadError, setLoadError ] = React.useState<boolean>(false);

  const fetchImage = async () => {
    setLoadError(false);

    // Only run if s3Key is valid
    if (s3Key && s3Key.length > 0) {
      const key = level === "public" ? `public/${s3Key}` : s3Key;

      // Attempt to get the requested image from S3 using Amplify Storage
      Storage.downloadData({ path: key }).result
        .then(async data => {
          if (typeof data === "string") {
            return setImgData(data);
          }

          const typedResult = await data.body.blob();
          const base64Data = await blobToBase64(typedResult);

          setImgData(base64Data as string);
        })
        .catch(_error => {
          setLoadError(true);
        });
    }
  };

  // Load the requested image (on mount and on key change)
  React.useEffect(() => {
    fetchImage();
  }, [ s3Key ]);

  return (
    <Image
      className={[ className, !imgData && placeholder ? "placeholder" : "" ].join(" ")}
      src={imgData || placeholder || "/assets/mezze-small-white.svg"}
      hidden={!!s3Key && !imgData && !loadError}
      alt={alt}
    />
  );
};

export const ImageWrapper = styled.div`
  ${({ theme }) => css`
    position: relative;
    background-color: ${theme.colors.white};
    padding-bottom: 100%;

    img {
      height: auto;
      width: auto;
      transform: translate(-50%, -50%);
      top: 50%;
      left: 50%;
      max-width: 100%;
      max-height: 100%;
      position: absolute;
    }
    &.placeholder img {
      width: 30px;
    }
  `}
`;

const Image = styled.img`
  display: block;
  width: 100%;

  &.placeholder {
    position: absolute;
    top: 50%;
    width: 30px;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

export default S3Image;
